import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  API,
  defaultLimitPagination,
  get,
  TableClasses,
  TableWrapClasses,
  TDClasses,
  THClasses,
  TRClasses,
} from "../../../config";
import moment from "moment";
import BootstrapTable from "react-bootstrap-table-next";
import Pagination from "../../../components/Pagination";
import MainBody from "../../../components/MainBody/MainBody";
import { CaretDownIcon, CaretRightIcon } from "@radix-ui/react-icons";
import BankTransactionByBatch from "./BankTransactionByBatch";
import UserDetailPopup from "./UserDetailPopup";

const ImportHistory = ({ trustId, isDesktop }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const defaultPage = query.get("page");
  const defaultLimit = query.get("limit");
  const [bankTransactionBatches, setBankTransactionBatches] = useState({});
  const [loading, setLoading] = useState(false);
  const [userModal, setUserModal] = useState(false);
  const [userId, setUserId] = useState([]);
  const [page, setPage] = useState(defaultPage || 1);
  const [limit, setLimit] = useState(defaultLimit || defaultLimitPagination);
  const [total, setTotal] = useState();
  const [from, setFrom] = useState();
  const [to, setTo] = useState();

  const toggleShowModal = () => {
    setUserModal(!userModal);
  };

  // *API Call for Bank Transaction Batches List - Start
  const fetchBankTransactionsBatches = useCallback(
    async (passLimit, passPage) => {
      try {
        setLoading(true);
        const { data } = await get(
          `${API.BANK_TRANSACTION_BATCHES}?trust_id=${
            trustId ? trustId : ""
          }&limit=${
            passLimit ? passLimit : limit ? limit : ""
          }&page=${passPage ? passPage : page ? page : ""}`
        );
        setLimit(data?.data?.per_page);
        setPage(data?.data?.current_page);
        setFrom(data?.data?.from);
        setTo(data?.data?.to);
        setTotal(data?.data?.total);
        setBankTransactionBatches(data?.data?.data);
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [trustId]
  );

  useEffect(() => {
    fetchBankTransactionsBatches();
  }, [fetchBankTransactionsBatches]);
  // *API Call for Bank Transaction Batches List - End

  useEffect(() => {
    navigate(
      `?tab=audit-trail&childTab=import-history&limit=${
        limit ? limit : defaultLimitPagination
      }&page=${page ? page : 1}`,
      {
        replace: true,
      }
    );
  }, [navigate, limit, page]);

  // *Bank Transaction Batches List Table Row and Column Generation, Filtering and Sorting of Record, Select Row and Expand Row - Start
  const bankTransactionBatchesGenerator = (quantity) => {
    const items = [];
    for (let i = 0; i < quantity; i++) {
      items.push({
        key: bankTransactionBatches[i]?.id,
        batch_id: bankTransactionBatches[i]?.id
          ? bankTransactionBatches[i]?.id
          : "",
        date: bankTransactionBatches[i]?.created_at
          ? moment(
              bankTransactionBatches[i]?.created_at,
              "DD-MM-YYYY HH:mm:ss"
            ).format("DD-MM-YYYY")
          : "-",
        description: bankTransactionBatches[i]?.description ? (
          <span
            onClick={(e) => {
              const id = e?.target?.attributes?.["data-id"];
              if (id?.value) {
                toggleShowModal();
                setUserId(id?.value);
              }
            }}
            data-toggle="modal"
            data-target="#userDetail"
            className="[&>a]:border-dashed [&>a]:border-b [&>a]:border-black dark:[&>a]:border-white"
            dangerouslySetInnerHTML={{
              __html: bankTransactionBatches[i]?.description,
            }}
          ></span>
        ) : (
          "-"
        ),
        total_records: bankTransactionBatches[i]?.total_records
          ? bankTransactionBatches[i]?.total_records.toLocaleString("en-US")
          : "",
      });
    }
    return items;
  };

  const bankTransactionBatchesData = bankTransactionBatchesGenerator(
    bankTransactionBatches?.length
  );

  const columns = [
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "date",
      text: "Date",
      style: { width: "15%" },
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "description",
      text: "Description",
      style: { width: "70%" },
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "total_records",
      text: "Number Of Records",
      align: "right",
      style: { width: "15%" },
    },
  ];

  const handleUpdateLimit = (e) => {
    const limit = e.target.value;
    setLimit(limit);
    setPage(1);
    fetchBankTransactionsBatches(limit, 1);
  };

  const handleChangePage = (page) => {
    setPage(page);
    fetchBankTransactionsBatches(limit, page);
  };

  const expandRow = {
    renderer: (row) => (
      <>
        <BankTransactionByBatch id={row?.batch_id} expandList={true} />
      </>
    ),
    onlyOneExpanding: true,
    showExpandColumn: true,
    expandByColumnOnly: true,
    headerClasses: THClasses,
    expandColumnRenderer: ({ expanded }) => {
      if (expanded) {
        return <CaretDownIcon />;
      }
      return <CaretRightIcon />;
    },
    className: TDClasses,
    expandHeaderColumnRenderer: () => {
      return <></>;
    },
  };
  return (
    <>
      <div className="relative">
        <MainBody
          search={false}
          innerBody={true}
          actionAlign={"end"}
          parentHeight={true}
          actionContent={""}
          loading={loading}
        >
          <div className="custom-table">
            <div className="table-body-class max-h-[600px]">
              <BootstrapTable
                keyField="batch_id"
                remote
                data={bankTransactionBatchesData}
                expandRow={expandRow}
                columns={columns}
                rowClasses={TRClasses}
                noDataIndication="No Data Found"
                classes={TableClasses}
                wrapperClasses={TableWrapClasses}
              />
            </div>

            <Pagination
              total={total}
              limit={parseInt(limit)}
              currentPage={page}
              updateLimit={handleUpdateLimit}
              updatePage={handleChangePage}
              from={from}
              to={to}
            />
            {userModal && (
              <UserDetailPopup
                id={userId}
                showView={userModal}
                setShowView={setUserModal}
              />
            )}
          </div>
        </MainBody>
      </div>
    </>
  );
};

export default ImportHistory;
